import { useEffect, useState } from "react";
import { useZone } from "../context/ZoneContext";
import { Sun, Cloud, CloudRain, CloudLightning, CloudFog, CloudSnow, Thermometer, Clock, Newspaper, Car, Construction, TrafficCone } from "lucide-react";

const CITIES = [
  { name: "Douala", lat: 4.05, lng: 9.70 },
  { name: "Yaoundé", lat: 3.87, lng: 11.52 },
  { name: "Bafoussam", lat: 5.48, lng: 10.42 },
  { name: "Bamenda", lat: 5.96, lng: 10.15 },
  { name: "Bertoua", lat: 4.58, lng: 13.68 },
  { name: "Ngaoundéré", lat: 7.32, lng: 13.58 },
  { name: "Garoua", lat: 9.30, lng: 13.40 },
  { name: "Maroua", lat: 10.59, lng: 14.32 },
];

// Zone Europe — Bruxelles (marché prioritaire) en tête.
const EU_CITIES = [
  { name: "Bruxelles", lat: 50.85, lng: 4.35 },
  { name: "Anvers", lat: 51.22, lng: 4.40 },
  { name: "Gand", lat: 51.05, lng: 3.72 },
  { name: "Liège", lat: 50.63, lng: 5.58 },
  { name: "Charleroi", lat: 50.41, lng: 4.44 },
  { name: "Paris", lat: 48.86, lng: 2.35 },
  { name: "Amsterdam", lat: 52.37, lng: 4.90 },
];

// Axes réputés chargés aux heures de pointe (advisory — pas de capteur temps réel au Cameroun)
const CM_TRAFFIC = {
  Douala: ["Carrefour Ndokoti", "Pont sur le Wouri", "Axe lourd Bonabéri", "Carrefour Deïdo"],
  Yaoundé: ["Carrefour Nlongkak", "Poste Centrale → Mvan", "Carrefour Mvog-Mbi", "Biyem-Assi"],
};

const NEWS = [
  "🛵 Plus de 1000 chauffeurs vérifiés à votre service",
  "🎉 Lancement : 3 mois 100% gratuits — profitez-en !",
  "💳 Payez en Orange Money, MTN Mobile Money ou espèces",
  "📍 Suivez votre chauffeur en temps réel sur la carte",
  "🛡️ Chaque chauffeur est couvert par sa propre assurance automobile",
  "📦 Envoi de colis express dans toute la ville",
  "☎️ Une question ? Écrivez à contact@bentskingo.com",
];

const codeInfo = (c) => {
  if (c === 0) return { Icon: Sun, label: "Ensoleillé" };
  if ([1, 2, 3].includes(c)) return { Icon: Cloud, label: "Nuageux" };
  if ([45, 48].includes(c)) return { Icon: CloudFog, label: "Brouillard" };
  if ([51, 53, 55, 61, 63, 65, 80, 81, 82].includes(c)) return { Icon: CloudRain, label: "Pluie" };
  if ([95, 96, 99].includes(c)) return { Icon: CloudLightning, label: "Orage" };
  if ([71, 73, 75, 77, 85, 86].includes(c)) return { Icon: CloudSnow, label: "Neige" };
  return { Icon: Cloud, label: "Météo" };
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function WeatherBanner() {
  const { zone } = useZone(); // "africa" | "europe" (IP + override super admin)
  const [rows, setRows] = useState([]);
  const [news, setNews] = useState(NEWS.map((t) => ({ text: t, link: "" })));
  const [traffic, setTraffic] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Zone fournie par ZoneContext (détection IP autoritaire + override super admin)

  // Actus : Cameroun → flux local éditable ; Europe → messages de marque uniquement
  useEffect(() => {
    if (zone !== "africa") return;
    const load = () => fetch(`${BACKEND}/api/ticker-news`)
      .then((r) => r.json())
      .then((j) => { if (Array.isArray(j.items) && j.items.length) setNews(j.items); })
      .catch(() => {});
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, [zone]);

  // Météo réelle (open-meteo) selon la zone détectée
  useEffect(() => {
    if (!zone) return;
    let ok = true;
    const cities = zone === "europe" ? EU_CITIES : CITIES;
    const lats = cities.map((c) => c.lat).join(",");
    const lngs = cities.map((c) => c.lng).join(",");
    const load = () =>
      fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lats}&longitude=${lngs}&current=temperature_2m,weather_code`)
        .then((r) => r.json())
        .then((j) => {
          if (!ok) return;
          const arr = Array.isArray(j) ? j : [j];
          setRows(cities.map((c, i) => ({ ...c, ...(arr[i]?.current || {}) })).filter((d) => d.temperature_2m != null));
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => { ok = false; clearInterval(t); };
  }, [zone]);

  // Trafic belge en direct (Zone Europe uniquement)
  useEffect(() => {
    if (zone !== "europe") return;
    let ok = true;
    const load = () => fetch(`${BACKEND}/api/traffic/be`)
      .then((r) => r.json())
      .then((j) => { if (ok) setTraffic(j); })
      .catch(() => {});
    load();
    const t = setInterval(load, 180000);
    return () => { ok = false; clearInterval(t); };
  }, [zone]);

  if (!rows.length) return null;

  const timeStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
  const RAIN = [51, 53, 55, 61, 63, 65, 80, 81, 82, 95, 96, 99];
  const isEu = zone === "europe";
  // Heure de pointe (WAT = UTC+1) : matin 6-9h, soir 16-20h → advisory trafic Cameroun
  const watHour = (now.getUTCHours() + 1) % 24;
  const rushCm = !isEu && ((watHour >= 6 && watHour < 9) || (watHour >= 16 && watHour < 20));
  const Sep = () => <span className="text-white/20 px-3" aria-hidden>•</span>;

  const TrafficCm = ({ dup }) => {
    if (isEu || !rushCm) return null;
    return (
      <div data-testid={dup === 0 ? "traffic-cm" : undefined} className="flex items-center">
        <div className="flex items-center gap-2 px-5 shrink-0">
          <TrafficCone className="w-5 h-5 text-[#F26419]" strokeWidth={2} />
          <span className="font-bold text-white text-sm">Trafic · heure de pointe</span>
          <Sep />
        </div>
        {Object.entries(CM_TRAFFIC).map(([city, axes], ci) => (
          <div key={`${dup}-cm-${ci}`} data-testid={dup === 0 ? `traffic-cm-${city}` : undefined} className="flex items-center gap-2 px-5 shrink-0">
            <Car className="w-5 h-5 text-[#D32F2F]" strokeWidth={2} />
            <span className="text-white text-sm font-semibold">{city}</span>
            <span className="text-white/60 text-xs">{axes.join(" · ")}</span>
            <Sep />
          </div>
        ))}
        <div className="flex items-center gap-2 px-5 shrink-0">
          <span className="text-[#F26419] text-sm font-semibold">⚠️ Roulez prudemment</span>
          <Sep />
        </div>
      </div>
    );
  };


  const TrafficEu = ({ dup }) => {
    if (!isEu || !traffic) return null;
    const jams = traffic.jams || [];
    const hasIssue = (traffic.jam_count || 0) > 0 || (traffic.roadworks_count || 0) > 0;
    return (
      <div data-testid={dup === 0 ? "traffic-eu" : undefined} className="flex items-center">
        <div className="flex items-center gap-2 px-5 shrink-0">
          <TrafficCone className="w-5 h-5 text-[#F26419]" strokeWidth={2} />
          <span className="font-bold text-white text-sm">Trafic Belgique</span>
          <Sep />
        </div>
        {jams.slice(0, 3).map((j, i) => (
          <div key={`${dup}-jam-${i}`} data-testid={dup === 0 ? `traffic-eu-jam-${i}` : undefined} className="flex items-center gap-2 px-5 shrink-0">
            <Car className="w-5 h-5 text-[#D32F2F]" strokeWidth={2} />
            <span className="text-white text-sm font-semibold">Bouchon {j.length_km} km</span>
            <span className="text-[#F26419] text-xs font-semibold">~{j.delay_min} min</span>
            <Sep />
          </div>
        ))}
        {(traffic.roadworks_count || 0) > 0 && (
          <div data-testid={dup === 0 ? "traffic-eu-roadworks" : undefined} className="flex items-center gap-2 px-5 shrink-0">
            <Construction className="w-5 h-5 text-[#FDB813]" strokeWidth={2} />
            <span className="text-white text-sm font-semibold">{traffic.roadworks_count} chantiers</span>
            <span className="text-white/50 text-xs">en cours</span>
            <Sep />
          </div>
        )}
        {!hasIssue && (
          <div className="flex items-center gap-2 px-5 shrink-0">
            <span className="text-[#16a34a] text-sm font-semibold">🟢 Trafic fluide</span>
            <Sep />
          </div>
        )}
        <div data-testid={dup === 0 ? "traffic-eu-prudence" : undefined} className="flex items-center gap-2 px-5 shrink-0">
          <span className="text-[#F26419] text-sm font-semibold">⚠️ Roulez prudemment</span>
          <Sep />
        </div>
      </div>
    );
  };

  const Sequence = ({ dup }) => (
    <div className="flex items-center" aria-hidden={dup !== 0 ? true : undefined}>
      <div data-testid={dup === 0 ? "clock-chip" : undefined} className="flex items-center gap-2 px-5 shrink-0">
        <Clock className="w-5 h-5 text-[#F26419]" strokeWidth={2} />
        <span className="font-bold text-white text-sm tabular-nums">{timeStr}</span>
        <span className="text-white/50 text-xs capitalize">{dateStr}</span>
        <Sep />
      </div>
      {rows.map((d, i) => {
        const { Icon, label } = codeInfo(d.weather_code);
        const bad = RAIN.includes(d.weather_code);
        return (
          <div key={`${dup}-w-${d.name}-${i}`} data-testid={dup === 0 ? `weather-city-${d.name}` : undefined} className="flex items-center gap-2 px-5 shrink-0">
            <Icon className="w-5 h-5 text-[#F26419]" strokeWidth={2} />
            <span className="font-bold text-white text-sm">{d.name}</span>
            <span className="text-white text-sm font-semibold">{Math.round(d.temperature_2m)}°C</span>
            <span className="text-white/50 text-xs">{label}</span>
            {bad && !isEu && <span className="text-[#F26419] text-xs font-semibold">⚠️ Roulez prudemment</span>}
            <Sep />
          </div>
        );
      })}
      <TrafficCm dup={dup} />
      <TrafficEu dup={dup} />
      {news.map((n, i) => {
        const inner = (
          <>
            <Newspaper className="w-4 h-4 text-[#FDB813]" strokeWidth={2} />
            <span className="text-white/90 text-sm">{n.text}</span>
            <Sep />
          </>
        );
        return n.link ? (
          <a key={`${dup}-n-${i}`} data-testid={dup === 0 && i === 0 ? "news-chip" : undefined} href={n.link} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 shrink-0 hover:text-[#FDB813]">
            {inner}
          </a>
        ) : (
          <div key={`${dup}-n-${i}`} data-testid={dup === 0 && i === 0 ? "news-chip" : undefined} className="flex items-center gap-2 px-5 shrink-0">
            {inner}
          </div>
        );
      })}
    </div>
  );

  return (
    <div data-testid="weather-banner" data-zone={zone} className="relative overflow-hidden bg-gradient-to-r from-[#1A1A1A] to-[#2A322C] border-y border-white/10">
      <div className="flex items-stretch">
        <div className="shrink-0 z-10 flex items-center gap-2 bg-[#F26419] px-4 py-2.5 text-white text-xs font-black uppercase tracking-wide">
          <Thermometer className="w-4 h-4" /> En direct
        </div>
        <div data-testid="fixed-clock" className="shrink-0 z-10 hidden sm:flex items-center gap-1.5 bg-[#111814] px-4 text-white text-sm font-bold tabular-nums border-r border-white/10">
          <Clock className="w-4 h-4 text-[#F26419]" /> {timeStr}
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="flex w-max whitespace-nowrap py-2.5 animate-[wbmarquee_60s_linear_infinite] hover:[animation-play-state:paused]">
            <Sequence dup={0} />
            <Sequence dup={1} />
          </div>
        </div>
      </div>
      <style>{`@keyframes wbmarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
    </div>
  );
}
